import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Search, UserPlus, X, Mail, Link2, Loader2 } from "lucide-react";
// Sprint B1b: league invite emails går via send-customer-email edge fn.

interface LeagueRegistrationProps {
  leagueId: string;
  leagueName?: string;
  onClose: () => void;
}

export default function LeagueRegistration({ leagueId, leagueName, onClose }: LeagueRegistrationProps) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [teamName, setTeamName] = useState("");

  // Invite state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhone, setInvitePhone] = useState("");

  // Search players
  const { data: players } = useQuery({
    queryKey: ["search-players", search],
    queryFn: async () => {
      if (search.length < 2) return [];
      // INTENTIONAL but flagged for product review: bucket-C reads phone in
      // a PLAYER-FACING context (partner-search by phone in league registration).
      // Visible only to admins per the existing RLS — the player-facing UI
      // wraps this in an admin-only feature gate elsewhere. Stays on
      // public.profiles. FOLLOW-UP: consider whether phone-search belongs in
      // player UI at all, or only in admin-managed flows.
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, phone, level")
        .neq("id", user!.id)
        .or(`full_name.ilike.%${search}%,phone.ilike.%${search}%`)
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: search.length >= 2,
  });

  const invalidateTeamQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["my-team-in-league"] });
    queryClient.invalidateQueries({ queryKey: ["my-league-teams"] });
    queryClient.invalidateQueries({ queryKey: ["league-team-count"] });
  };

  // TASK-67 PR-D: self-register flow rewritten to use team_members as the
  // canonical roster source. The teams.player1_id / player2_id /
  // player1_customer_id / player2_customer_id columns are dropped in PR-E
  // (and the matching teams_player1_identity_chk / player2_identity_chk
  // CHECK constraints with them). Until PR-E lands, INSERTs that omit
  // player1_id will fail the CHECK — accepted brief-window risk per
  // audit-reports/team-members-equality/01-investigation.md §12 risk matrix
  // ("Refactor LeagueRegistration in PR-D; column drop in PR-E after Vercel
  // deploy"). Admin can manually create teams during the gap if needed.
  //
  // Both write paths (selected partner + invite-by-email) now:
  //   1. INSERT a teams row WITHOUT any player slot columns and WITHOUT
  //      the legacy invite columns (player2_invite_email + player2_invite_name
  //      are dropped by this PR's migration 20260504110000).
  //   2. INSERT a team_members row for the registering user
  //      (role='primary'). This is the equality-based replacement for the
  //      "creator becomes player1" implicit semantic.
  //   3a. Selected partner path: INSERT a SECOND team_members row for the
  //       selected user (role='primary'). Both members are equal — no
  //       captain or "primary slot 1 vs 2" semantic.
  //   3b. Invite path: INSERT a team_invitations row with intended_role
  //       ='primary' + the partner's email. The invite RPC
  //       (accept_team_invitation) will materialize the team_members row
  //       on accept.
  //
  // The previously-written invite_token + invite_sent_at columns are no
  // longer needed because team_invitations carries its own invite_token
  // (UNIQUE) per row and the join URL is built from that.

  // TASK-67 PR-D Feature 1: auto-generate team_name from primary
  // players' full_names (joined with "/") when the user leaves the
  // "Lagnamn" field empty. Mirrors the same logic in
  // AdminTeams.createTeamMutation. The registering user is always
  // primary #1; selectedPlayer (when present) is primary #2.
  // For the invite-by-email path only the registering user is a
  // primary at create time — the invitee becomes one when they
  // accept via accept_team_invitation RPC. The user can edit the
  // name later via the TeamCard pencil-edit (Feature 2).
  async function resolveAutoTeamName(partnerName: string | null): Promise<string> {
    const trimmed = teamName.trim();
    if (trimmed) return trimmed;
    // Fetch registering user's full_name from profiles (auth metadata
    // may not always carry it). Best-effort: fall back to empty string
    // so we end up with team_name=null rather than a bad label.
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("id", user!.id)
      .maybeSingle();
    const myName = profile?.full_name?.trim() ?? "";
    const names = [myName, partnerName?.trim() ?? ""].filter((n) => n.length > 0);
    return names.join("/");
  }

  // Register with selected player
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPlayer) throw new Error("Välj en medspelare");
      // TASK-67 PR-D Feature 1: compute auto-name BEFORE the team INSERT.
      const computedName = await resolveAutoTeamName(selectedPlayer.full_name ?? null);
      // TASK-67 PR-D: INSERT teams WITHOUT player1/2 + customer slots.
      const { data: createdTeam, error } = await supabase.from("teams").insert({
        league_id: leagueId,
        team_name: computedName || null,
        payment_status: "pending",
      } as never)
        .select("id")
        .single();
      if (error) throw error;
      const teamId = (createdTeam as { id: string } | null)?.id;
      if (!teamId) throw new Error("Lag-id saknas efter insert");

      // TASK-67 PR-D: INSERT 2x team_members rows — registering user +
      // selected partner — both as role='primary'. team_members is the
      // canonical source post-migration; pickFirstTwoPrimaries picks the
      // first 2 by joined_at for any "first 2 players" UI.
      const { error: memberErr } = await supabase
        .from("team_members")
        .insert([
          { team_id: teamId, user_id: user!.id, role: "primary" },
          { team_id: teamId, user_id: selectedPlayer.id, role: "primary" },
        ]);
      if (memberErr) {
        // Best-effort rollback — no real transaction without an RPC.
        await supabase.from("teams").delete().eq("id", teamId);
        throw memberErr;
      }
    },
    onSuccess: () => {
      toast({ title: "Anmälan skickad!", description: "Ni är nu anmälda till serien." });
      invalidateTeamQueries();
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Fel", description: err.message, variant: "destructive" });
    },
  });

  // Register with invite (no partner yet — invite via email)
  const inviteMutation = useMutation({
    mutationFn: async ({ copyLink }: { copyLink?: boolean } = {}) => {
      if (!inviteEmail && !copyLink) throw new Error("Ange e-postadress");
      // TASK-67 PR-D: each team_invitations row carries its own invite_token
      // (UNIQUE NOT NULL on the table). We reuse it to build the join URL
      // both for the email body and the copy-link path. inviteFirstName +
      // inviteLastName are kept in form state for future "name on invite"
      // UX but not persisted — the legacy player2_invite_name column was
      // dropped by this PR's migration and team_invitations doesn't carry
      // an invitee name today.
      const inviteToken = crypto.randomUUID();

      // TASK-67 PR-D: INSERT teams WITHOUT player slot columns and
      // WITHOUT the dropped invite columns (player2_invite_email,
      // player2_invite_name — gone in this PR's migration). The teams row
      // is now identity-free; team_members + team_invitations carry the
      // roster + outstanding invites.
      // TASK-67 PR-D Feature 1: auto-generate team_name from registering
      // user's full_name only (single primary at create time; the invitee
      // becomes a primary on accept). Caller can edit later via TeamCard.
      const computedName = await resolveAutoTeamName(null);
      const { data: createdTeam, error } = await supabase
        .from("teams")
        .insert({
          league_id: leagueId,
          team_name: computedName || null,
          payment_status: "pending",
        } as never)
        .select("id")
        .single();
      if (error) throw error;
      const teamId = (createdTeam as { id: string } | null)?.id;
      if (!teamId) throw new Error("Lag-id saknas efter insert");

      // TASK-67 PR-D: INSERT team_members row for the registering user
      // (role='primary'). The invited partner gets a team_members row when
      // they accept the invitation via accept_team_invitation RPC.
      const { error: memberErr } = await supabase
        .from("team_members")
        .insert({ team_id: teamId, user_id: user!.id, role: "primary" });
      if (memberErr) {
        await supabase.from("teams").delete().eq("id", teamId);
        throw memberErr;
      }

      // TASK-67 PR-D: INSERT team_invitations row (intended_role='primary').
      // Prior to PR-D this was tracked via teams.player2_invite_email +
      // invite_token columns; those are gone now. team_invitations is the
      // single source of truth for outstanding invites and powers both the
      // legacy email link and the new copy-link button via its own
      // invite_token.
      const { error: inviteErr } = await supabase
        .from("team_invitations")
        .insert({
          team_id: teamId,
          invited_by_user_id: user!.id,
          invited_email: inviteEmail || null,
          invite_token: inviteToken,
          intended_role: "primary",
        } as never);
      if (inviteErr) {
        // Roll back team_members + teams on invite failure — invite is the
        // only way the partner ever joins, so no partial state.
        await supabase.from("team_members").delete().eq("team_id", teamId);
        await supabase.from("teams").delete().eq("id", teamId);
        throw inviteErr;
      }

      const inviteUrl = `${window.location.origin}/join-team/${inviteToken}`;

      if (copyLink) {
        await navigator.clipboard.writeText(inviteUrl);
        return { mode: "link" as const };
      }

      // Sprint B1b: server-side dispatcher hämtar invite-email + token från
      // teams-raden, validerar player1_id ownership, bygger inviteUrl från
      // org primary domain, renderar template. Inga client-supplied to/url.
      // TASK-67 PR-D: send-customer-email's league_team_invite template
      // historically read teams.player2_invite_email + invite_token. Those
      // columns are gone — the template needs to read from team_invitations
      // instead (handled in PR-C edge fn refactor / dispatcher).
      if (inviteEmail) {
        const { error: emailErr } = await supabase.functions.invoke("send-customer-email", {
          body: { template: "league_team_invite", teamId },
        });
        if (emailErr) console.error("[LEAGUE INVITE EMAIL ERROR]", emailErr);
      }

      return { mode: "email" as const };
    },
    onSuccess: (result) => {
      if (result?.mode === "link") {
        toast({ title: "Länk kopierad!", description: "Dela länken med din medspelare." });
      } else {
        toast({ title: "Inbjudan skickad!", description: `Inbjudan skickad till ${inviteEmail}` });
      }
      invalidateTeamQueries();
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Fel", description: err.message, variant: "destructive" });
    },
  });

  const isInviteValid = inviteEmail.includes("@") || (inviteFirstName && inviteLastName);
  const isPending = registerMutation.isPending || inviteMutation.isPending;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Anmäl lag</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Team name */}
        <div className="space-y-1.5">
          <Label>Lagnamn (valfritt)</Label>
          <Input
            placeholder="T.ex. Padel Kings"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
          />
        </div>

        {/* Partner search */}
        <div className="space-y-1.5">
          <Label>Välj medspelare *</Label>
          {selectedPlayer ? (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="font-medium">{selectedPlayer.full_name}</p>
                <p className="text-sm text-muted-foreground">Nivå {selectedPlayer.level}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedPlayer(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : !showInviteForm ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Sök namn eller telefon..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {players && players.length > 0 && (
                <div className="rounded-md border divide-y max-h-48 overflow-y-auto">
                  {players.map((p: any) => (
                    <button
                      key={p.id}
                      className="flex items-center justify-between w-full p-3 text-left hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        setSelectedPlayer(p);
                        setSearch("");
                      }}
                    >
                      <div>
                        <p className="font-medium text-sm">{p.full_name || "Okänd"}</p>
                        <p className="text-xs text-muted-foreground">Nivå {p.level}</p>
                      </div>
                      <UserPlus className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
              {search.length >= 2 && (!players || players.length === 0) && (
                <p className="text-sm text-muted-foreground">Inga spelare hittades</p>
              )}

              {/* Invite alternatives */}
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Hittar du inte din medspelare?</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => setShowInviteForm(true)}
                  >
                    <Mail className="h-3.5 w-3.5" /> Bjud in via mail
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    disabled={isPending}
                    onClick={() => inviteMutation.mutate({ copyLink: true })}
                  >
                    <Link2 className="h-3.5 w-3.5" /> Kopiera inbjudningslänk
                  </Button>
                </div>
              </div>
            </>
          ) : (
            /* Invite form */
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Bjud in medspelare</p>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowInviteForm(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Förnamn</Label>
                  <Input
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Efternamn</Label>
                  <Input
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">E-post *</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="namn@example.se"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Telefon (valfritt)</Label>
                <Input
                  value={invitePhone}
                  onChange={(e) => setInvitePhone(e.target.value)}
                  placeholder="07X XXX XX XX"
                  className="h-8 text-sm"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                En inbjudan skickas. Din medspelare skapar konto och kopplas automatiskt till ert lag.
              </p>
            </div>
          )}
        </div>

        {/* Action button */}
        {selectedPlayer ? (
          <Button
            className="w-full"
            onClick={() => registerMutation.mutate()}
            disabled={isPending}
          >
            {registerMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Anmäler...</> : "Bekräfta anmälan"}
          </Button>
        ) : showInviteForm ? (
          <Button
            className="w-full"
            onClick={() => inviteMutation.mutate({})}
            disabled={!isInviteValid || isPending}
          >
            {inviteMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Skickar...</> : "Skicka inbjudan och anmäl"}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
